/**
 * shared/voice/script.ts — what she is asked to say, and why each one is there.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * **The phonetic-balance justification is WITHDRAWN.** The draft justified ten prompts as
 * phonetically balanced. Phonetic coverage matters when a system selects or models units, and
 * Chatterbox does none of that (§5.2): the reference reaches the model as mel statistics, an
 * x-vector, 375 speech tokens used as a style prefix, and a mean-pooled utterance embedding — none of
 * which is coverage-sensitive. A seed containing every English diphone and a seed containing the word
 * "aluminium" forty times condition all three paths identically in kind.
 *
 * What the script is actually for is smaller, real, and different: **it gives a nervous person
 * something to say so they produce ten to fifteen continuous seconds without stalling, and it fixes
 * register.** That is the whole claim now, and §7 adjudicates it — arm N is by construction
 * unscripted spontaneous speech, arm S is this script. §7 can delete this file.
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ── The four constraints every line below is written against ────────────────────────────────────
 *
 * 1. **Pictured, not decoded.** A reader who cannot picture what she is reading reads word-by-word,
 *    and word-by-word is the flattest possible seed. Every scene is one an Indian-English speaker of
 *    Karti's parents' generation has actually stood in. No proper nouns, no numbers, no words that
 *    invite careful articulation — a line that makes somebody slow down to get it right has already
 *    cost more prosody than its content is worth.
 * 2. **Sight-reading is the enemy, so the app speaks it first (§3.7, deviation 1 REVERSED).** Reading
 *    unfamiliar literary English aloud, cold, off a screen, to a machine, is a performance skill.
 *    Hearing a line and repeating it is mimicry, which is far easier and yields natural intonation.
 *    The text stays on screen; she reads along having already heard it.
 * 3. **The prompt audio is a real recording of Karti, deliberately flat.** Never a Chatterbox
 *    synthesis: generated audio carries a Perth watermark and playing it into a room with an open
 *    microphone is how VOX-LAW 27 gets violated by accident. And **mimicry copies melody, not just
 *    words** — a warmly-performed prompt in Karti's voice yields a seed carrying *Karti's* prosody in
 *    his mother's timbre. `docs/recording-the-prompts.md` is one paragraph about this.
 * 4. **Length.** Each scripted line is 35–45 words, which at a careful reader's ~2.4 words/s is
 *    15–19 s of speech. VOX-LAW 3 needs 10.000 continuous seconds measured from first onset, and
 *    `SEED_POLICY.targetSeconds` is 15.0, so a line that yields 12 s leaves nothing for a hesitation.
 *
 * ── Q1 is still open, and it is the only thing here a code change cannot recover ────────────────
 *
 * §17 Q1: read these aloud to Karti's parents and watch their faces before P1's gate. Five minutes,
 * worth more than any further literature search. There is now a measurement beside it — per-prompt
 * disfluency rate against the session median across the first five real sessions swaps a prompt
 * automatically (§3.7) — but the conversation happens first. `p07`..`p10` exist unused by either
 * session shape precisely so that swap has somewhere to swap to.
 */

// ─── versioning ──────────────────────────────────────────────────────────────

/**
 * Written onto **every** `voice_sessions.scriptVersion` row.
 *
 * ⚠️ **Bump it for any change to any `text` below, including punctuation.** The whole point of
 * versioning a script that lives in one file is that "we changed prompt 3 in September" is legible
 * afterwards at zero marginal cost — a before/after on disfluency rate, on read time, and on what the
 * resulting voices sounded like. A silent edit makes every session recorded on either side of it
 * uncomparable, which is exactly the data §7 and §3.7's swap rule are built on.
 *
 * Changing `why`, `targetSeconds` or the session shapes does NOT bump it. Only what she says does.
 */
export const SCRIPT_VERSION = 1;

// ─── the shape of an entry ───────────────────────────────────────────────────

/**
 * What a prompt is *for*, which decides what the resulting take's `kind` is and what the studio may
 * do with it. This is the intrinsic kind of the text; the session plan assigns the take kind.
 *
 *   consent    → `takes.kind = 'consent'`   the evidence a person agreed (VOX-LAW 22). Never a seed.
 *   unscripted → `takes.kind = 'baseline'`  `speakerBaseline` (§6.3) AND the likeliest best seed.
 *   scripted   → `takes.kind = 'candidate'` eligible for window selection.
 *   heldOut    → `takes.kind = 'heldOut'`   VOX-LAW 10. Enters no candidate seed, ever.
 */
export type PromptKind = "consent" | "unscripted" | "scripted" | "heldOut";

export interface Prompt {
  /** `voice_sessions`-stable and written on every take row. Never renumbered; retire, do not reuse. */
  id: string;
  /** What she says. The single source of truth for the screen, the ASR reference and the fixture WAV. */
  text: string;
  /** Why this one exists. Read this before replacing a prompt that "doesn't seem to land". */
  why: string;
  /** Expected speech duration, seconds. Used to size the wait screen, never to void a take. */
  targetSeconds: number;
  kind: PromptKind;
}

// ─── the two prompts that are not scenes ─────────────────────────────────────

/**
 * **Consent, first, and warm (§3.8).**
 *
 * The draft put an ASR-gated legal moment between her and the payoff, with a retry loop, "matched"
 * undefined, on a 0.6B streaming model, in Indian English. That screen would have been the most
 * alienating in the product and the only one able to refuse her. It moves to the FRONT, framed as the
 * first short thing to say rather than a gate before a prize.
 *
 * Written short, warm and phonetically easy on purpose, because it is the one line whose transcript
 * has to match (`CONSENT_WER_MAX` in policy.ts) — and everything about the mechanism around it is
 * built so that a poor match never becomes a refusal she experiences.
 *
 * **VOX-LAW 22: this recording, in the subject's own voice, is the ONLY consent this product accepts.**
 * No dialog, no checkbox, no "I have permission" toggle a grandchild can click on someone else's
 * behalf. The evidence that a person agreed to have their voice cloned is a recording of that person
 * saying so, kept forever, playable from `/admin`. It is why anon capture of a third party is safe to
 * offer at all.
 */
export const CONSENT_PROMPT: Prompt = Object.freeze({
  id: "consent",
  text: "This is my voice, and I'm happy for my family to use it.",
  why:
    "The one line that must be verifiable. Short so it is easy to say cleanly; plain so a 0.6B " +
    "streaming FastConformer on Indian English has a chance at it; first so it is a warm-up rather " +
    "than a gate standing between her and the payoff.",
  targetSeconds: 4,
  kind: "consent",
});

/**
 * **The unscripted prompt, and it is doing four jobs at once.**
 *
 * Q6's own argument leads here and the draft did not follow it to the end: a reader who cannot
 * picture what she is reading reads word-by-word, and word-by-word is the flattest possible seed —
 * therefore **the best seed is probably not read at all.** The most fluent, most prosodically varied
 * fifteen seconds this product will ever collect from a 68-year-old is her talking about something
 * she knows. `derive/vad.ts` cuts a clean continuous window out of a 45-second ramble, and VOX-LAW 3
 * needs only 10.000 continuous seconds.
 *
 * Disfluency is not a defect; it is prosody. A self-correction — *"the evening train, sorry, the
 * evening train"* — must never be rejected, which is why the per-take transcript check is advisory
 * and only the consent take must match.
 *
 * The four jobs:
 *   - it is the most likely source of the best seed,
 *   - it is `speakerBaseline`, the prosodic reference the atypicality penalty needs (§6.3),
 *   - it is the only recording of the person *not performing*, which makes it the correct control for
 *     every future question about the studio, including ones not yet asked,
 *   - and it is the closest in-product analogue of §7's arm N.
 *
 * ⚠️ **It is NOT arm N, and conflating them would invalidate §7.** Arm N must be collected before the
 * speaker has ever seen the studio, with no meter, no cue, no retake and no coaching, from `/trial`.
 * This one happens after a room check and a cue and is therefore already a studio artifact. Both are
 * recorded and both are kept.
 *
 * It may be answered in any language (§3.8). A Hindi or Gujarati ramble is a *better* seed than a
 * stiff English one, and the ASR result on it is advisory anyway.
 */
export const UNSCRIPTED_PROMPT: Prompt = Object.freeze({
  id: "unscripted",
  text: "Tell me about the house you grew up in.",
  why:
    "The likeliest source of the best seed in the whole session, and the only recording of this " +
    "person not performing. Everybody can answer it, nobody has to read it, and it produces long " +
    "continuous speech with real intonation. Answer it in any language.",
  targetSeconds: 45,
  kind: "unscripted",
});

// ─── the ten scenes ──────────────────────────────────────────────────────────

/**
 * Ten scripted prompts. A short session uses two of them, a long session six; the rest are the swap
 * pool (§3.7).
 *
 * Every one is a domestic scene from the right generation and the right country, written so that the
 * reader is remembering rather than parsing. The four §17 Q1 names them explicitly — the evening
 * train, tea in small glass cups, the river reaching the door, the photograph by the stairs — are
 * `p01`..`p04` and are the four to read aloud first.
 */
export const SCRIPTED_PROMPTS: readonly Prompt[] = Object.freeze([
  Object.freeze({
    id: "p01",
    text:
      "The evening train came in just as the light was going. You could hear it long before you saw " +
      "it, and everyone on the platform would stand up at the same moment, without anybody saying a word.",
    why:
      "A shared, unhurried memory with a natural rise and fall built into it. The last clause is a " +
      "long one, which is where the prosody lives.",
    targetSeconds: 16,
    kind: "scripted",
  }),
  Object.freeze({
    id: "p02",
    text:
      "They served the tea in small glass cups, so hot you had to hold them by the rim. Somebody " +
      "always poured a little into the saucer to cool it, and the room smelled of ginger all afternoon.",
    why:
      "Physical and sensory, so it is remembered rather than decoded. No word in it needs care to " +
      "pronounce, which is the point: a word that needs care costs a phrase of intonation.",
    targetSeconds: 16,
    kind: "scripted",
  }),
  Object.freeze({
    id: "p03",
    text:
      "The year the river came up to our door, we moved everything onto the beds and sat there " +
      "watching the water. It went down by the morning and left a fine grey line on the wall that " +
      "nobody ever painted over.",
    why:
      "The one prompt with a small amount of drama in it, which pulls the reader out of list " +
      "intonation. Ends quietly, so the take does not finish on a projected note.",
    targetSeconds: 18,
    kind: "scripted",
  }),
  Object.freeze({
    id: "p04",
    text:
      "There was a photograph on the wall by the stairs, and you passed it so often that you stopped " +
      "seeing it. Then one day somebody visits and asks who that is, and you stand there looking at " +
      "it as though it were new.",
    why:
      "Second person throughout, which is the register people use when telling somebody something " +
      "rather than reading at them.",
    targetSeconds: 18,
    kind: "scripted",
  }),
  Object.freeze({
    id: "p05",
    text:
      "On hot nights everybody carried their beds up to the terrace. You could hear other families " +
      "talking on their own roofs, and somewhere a radio, and you would fall asleep before anyone " +
      "got round to turning it off.",
    why: "Long, softly-falling final clause. Good material for Zone B, where the LM continues the rhythm.",
    targetSeconds: 17,
    kind: "scripted",
  }),
  Object.freeze({
    id: "p06",
    text:
      "The tailor kept a stub of chalk behind his ear and never once used a tape on me. He would " +
      "look, and nod, and say come back on Thursday, and on Thursday it fit better than anything I " +
      "have bought since.",
    why:
      "Carries a small joke, and a reader who finds a line slightly funny reads it as speech. The " +
      "three short verbs in the middle break the metre.",
    targetSeconds: 17,
    kind: "scripted",
  }),
  Object.freeze({
    id: "p07",
    text:
      "When the lights went out, the whole street went quiet for a second, and then everyone came " +
      "out onto their steps at once. Somebody would find the candles, and honestly those evenings " +
      "were nicer than the ones with the lights on.",
    why: "Swap pool. Warm ending, very common memory, no vocabulary anywhere near the edge of easy.",
    targetSeconds: 18,
    kind: "scripted",
  }),
  Object.freeze({
    id: "p08",
    text:
      "The kitchen before a wedding is the best place in the house. Four people are talking, nobody " +
      "is listening, something is always about to burn, and everyone keeps telling you to sit down " +
      "and then handing you something to stir.",
    why: "Swap pool. Present tense and a list that accelerates — a different rhythm from the rest.",
    targetSeconds: 18,
    kind: "scripted",
  }),
  Object.freeze({
    id: "p09",
    text:
      "The man who sold fruit came past at the same hour every day, calling out, and you could tell " +
      "the season from the sound of it. In summer he hardly had to call at all, because everybody " +
      "was already at the gate.",
    why: "Swap pool. About a sound, which tends to make people read it with more contour.",
    targetSeconds: 18,
    kind: "scripted",
  }),
  Object.freeze({
    id: "p10",
    text:
      "Morning was one long argument about shoes. Somebody could never find the second one, somebody " +
      "else had eaten and gone, and then the bus would sound its horn at the corner and all of it " +
      "stopped at once.",
    why: "Swap pool. Opens on a short flat sentence, which is a good place for a nervous reader to start.",
    targetSeconds: 16,
    kind: "scripted",
  }),
]);

// ─── held out ────────────────────────────────────────────────────────────────

/**
 * **VOX-LAW 10. Every similarity number is computed against a held-out take that entered no candidate
 * seed.** These two lines are recorded exactly like the scripted ones and are never eligible for a
 * window.
 *
 * Two design points that are easy to get wrong:
 *
 *  - **She is never told which one is held out.** If she knew, she would read it differently, and a
 *    reference recorded in a different register is not a reference. The on-screen framing for a
 *    held-out prompt is byte-identical to a scripted one (`shared/voice/copy.ts`).
 *  - **The text appears in no seed and in no eval set.** §7.2's eight eval sentences are drawn from
 *    neither this file nor arm H. If you add an eval sentence, check it against these two by hand;
 *    a similarity number computed against material the model was conditioned on is not a similarity
 *    number.
 */
export const HELD_OUT_PROMPTS: readonly Prompt[] = Object.freeze([
  Object.freeze({
    id: "h01",
    text:
      "The first bus of the morning always had the windows open, whatever the weather. You would get " +
      "on half asleep and be completely awake by the second stop, and nobody ever complained about it.",
    why:
      "Held out. Same register and same length as the scripted prompts so it is a fair reference, " +
      "and indistinguishable from them on screen so her delivery does not change.",
    targetSeconds: 15,
    kind: "heldOut",
  }),
  Object.freeze({
    id: "h02",
    text:
      "There was a shop at the end of the road that sold everything and found nothing. You would " +
      "ask, and he would say yes yes, and then move three boxes and come back with exactly what you " +
      "wanted.",
    why: "Held out, second one, used only by the long session.",
    targetSeconds: 15,
    kind: "heldOut",
  }),
]);

// ─── the whole script ────────────────────────────────────────────────────────

export const ALL_PROMPTS: readonly Prompt[] = Object.freeze([
  CONSENT_PROMPT,
  UNSCRIPTED_PROMPT,
  ...SCRIPTED_PROMPTS,
  ...HELD_OUT_PROMPTS,
]);

const BY_ID = new Map(ALL_PROMPTS.map((p) => [p.id, p]));

export class ScriptError extends Error {
  override name = "ScriptError";
}

/** Throws rather than returning undefined: a prompt id on a take row that does not resolve is a bug
 *  in the session plan, and a silent `undefined` there renders an empty screen with an open mic. */
export function promptById(id: string): Prompt {
  const p = BY_ID.get(id);
  if (!p) throw new ScriptError(`unknown promptId ${JSON.stringify(id)} (scriptVersion ${SCRIPT_VERSION})`);
  return p;
}

// ─── the two session shapes ──────────────────────────────────────────────────

export type SessionShape = "short" | "long";

/**
 * **The short session is *the* session (§3.5).**
 *
 * The draft made ten prompts the default and four the fallback; that is backwards, and the draft's
 * own falsifier said so. The realistic accounting for ten prompts — read 6 s + cue 3 s + speak
 * 12–18 s + decide 5 s ≈ 30 s each with no retake, plus permission, room check, consent, retakes and
 * the wait — lands at 12–16 minutes, above the 11-minute figure that falsified the design. And there
 * is no cohort to average over here: **the first five family sessions ARE Mom and Dad, and their
 * first impression is unrecoverable.**
 *
 *   short:  consent + unscripted + 2 scripted + 1 held out  = 5 recorded items,  ≤ 7 min to reveal
 *   long:   consent + unscripted + 6 scripted + 2 held out  = 10 recorded items, ≤ 14 min
 *
 * **The long session is not deleted, and the obvious objection — that if four prompts are enough then
 * ten are waste — is wrong because §7 needs arm S to be the full ten-prompt product**, or the
 * experiment measures a shortened studio rather than the studio. After the reveal the offer is warm
 * and optional: *"Want to do a few more, so it gets even closer?"* That is the moment a long session
 * is a pleasure rather than an audition.
 *
 * Order matters and is not alphabetical: consent first (§3.8, it is the warm-up), then the unscripted
 * prompt while she is least self-conscious and most likely to produce the best seed, then the scripted
 * ones, then the held-out one last — by which point she has settled, which is what a reference wants.
 */
export const SHORT_SESSION: readonly string[] = Object.freeze([
  "consent",
  "unscripted",
  "p01",
  "p02",
  "h01",
]);

export const LONG_SESSION: readonly string[] = Object.freeze([
  "consent",
  "unscripted",
  "p01",
  "p02",
  "p03",
  "p04",
  "p05",
  "p06",
  "h01",
  "h02",
]);

/** The ordered prompts for a shape. Throws on an unknown id, so a bad shape fails at boot not mid-session. */
export const sessionPlan = (shape: SessionShape): readonly Prompt[] =>
  (shape === "short" ? SHORT_SESSION : LONG_SESSION).map(promptById);

/**
 * Rough wall-clock estimate for a shape, in ms, for the front door and for `/admin`.
 *
 * Per item: the app plays the prompt (targetSeconds is a good proxy for how long Karti's flat reading
 * runs), the cue and its guard, her speech, and a beat before the next screen. Retakes and the wait
 * are not in here — this is the floor, not the prediction, and §3.5's ≤ 7 min target is measured
 * against `voice_sessions.completedAt − createdAt`, not against this.
 */
export function estimateSessionMs(shape: SessionShape): number {
  const perItemOverheadMs = 3000 + 3500; // the cue plus its guard, and a beat between screens
  return sessionPlan(shape).reduce(
    (ms, p) => ms + (p.kind === "unscripted" ? p.targetSeconds * 1000 : p.targetSeconds * 2000) + perItemOverheadMs,
    0,
  );
}

// ─── the prompt recordings ───────────────────────────────────────────────────

/**
 * Where the client fetches Karti's flat reading of a prompt. Served as a static asset from the client
 * bundle's public root, so it costs no route and no access check — the prompts are not anybody's
 * recording of anything private.
 *
 * ⚠️ **The WAVs do not exist yet, and their absence is a designed state rather than an oversight.**
 * `docs/recording-the-prompts.md` says how Karti records them and why flat; `samples/prompts/` is
 * where they land; P1's gate needs them because deviation 1 was REVERSED specifically so that a
 * nervous reader hears the line before she says it. Until they are recorded, the studio shows the
 * text and the "say it again" control is inert — which is deviation 1's original form, is a known
 * regression against the design, and is the single most likely reason a first session produces a flat
 * seed. It is not a fallback to settle for.
 */
export const PROMPT_AUDIO_DIR = "/prompts";
export const promptAudioUrl = (id: string): string => `${PROMPT_AUDIO_DIR}/${promptById(id).id}.wav`;
